import {parseFileAnchor} from '@github-ui/blob-anchor'
import type {CodeSection, CodeSectionLookup, DefinitionOrReference} from '@github-ui/code-nav'
import {useCopilotSettings} from '@github-ui/code-view-shared/contexts/CopilotSettingsContext'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import {useSplitCodeownersErrorsContext} from '@github-ui/code-view-shared/contexts/SplitCodeownersErrorsContext'
import {isFeatureEnabled} from '@github-ui/feature-flags'
// useLocation is safe for files not rendered in a partial on the overview.
// eslint-disable-next-line no-restricted-imports
import {useLocation} from '@github-ui/react-router'
import safeStorage from '@github-ui/safe-storage'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {ScopedCommands} from '@github-ui/ui-commands'
import {useCodeViewOptions} from '@github-ui/use-code-view-options'
import {clsx} from 'clsx'
import type React from 'react'
import {useCallback, useEffect, useEffectEvent, useLayoutEffect, useMemo, useRef, useState} from 'react'

import {useFindInFileOpen} from '../../../../contexts/FindInFileOpenContext'
import {useCurrentBlame} from '../../../../hooks/CurrentBlame'
import {useCurrentBlob} from '../../../../hooks/CurrentBlob'
import {useLineElementMap} from '../../../../hooks/CurrentLineRefMap'
import {useCurrentLineHeight} from '../../../../hooks/use-current-line-height'
import {useIsCursorEnabled} from '../../../../hooks/use-cursor-navigation'
import {useManualRender} from '../../../../hooks/use-manual-render'
import {type ScrollRequestPayload, useScrollLineIntoView} from '../../../../hooks/use-scroll-line-into-view'
import {useSharedLineObserver} from '../../../../hooks/use-shared-line-observer'
import {useStickyHeaderHeight} from '../../../../hooks/use-sticky-header-height'
import {DELETE_STICKY_LINES_VALUE, type SetStickyLinesType} from '../../../../hooks/use-sticky-lines'
import {useSuspenseStyledBlob} from '../../../../hooks/use-suspense-styled-blob'
import {type SymbolUnderPointer, useSymbolUnderPointer} from '../../../../hooks/use-symbol-under-pointer'
import {updateExpandPreferences} from '../../../../hooks/use-update-panel-expand-preferences'
import {
  applyStickyToParentStartLines,
  expandAllRows,
  expandRow,
  forceAnnouncementToScreenReaders,
  getActualLineNumberBinarySearch,
  isLineInViewport,
  isRowCollapsed,
} from '../../../../utilities/lines'
import {HiddenUnicodeAlert} from '../../Banners/HiddenUnicode'
import type {CodeNavData} from '../BlobContent'
import {CopilotButtonContainer} from '../CopilotButton'
import {HighlightedLineMenuContainer} from '../HighlightedLineMenu'
import {BlameLines, BlameLinesSSR} from './Blame'
import type {CodeLinesHandle} from './code-lines-handle'
import codeBlobStyles from './CodeBlob.module.css'
import {CodeLines} from './CodeLines'
import {CodeLinesNoVirtualization, useShouldDoNoVirtualization} from './CodeLinesNoVirtualization'
import {CodeLinesSSR} from './CodeLinesSSR'
import {CodeLinesSSRNoVirtualization} from './CodeLinesSSRNoVirtualization'
import {HighlightedLinesProvider} from './HighlightedLineProvider'
import {type CodeLineData, useCodeLines} from './hooks/use-code-lines'
import {useHashHighlightedLines} from './hooks/use-hash-highlighted-lines'
import {NavigationCursor, type NavigationCursorHandle} from './NavigationCursor'
import {SearchResultsProvider} from './SearchResultsProvider'
import {TextArea} from './TextArea'

const safeLocalStorage = safeStorage('localStorage')

const noOp = () => {}
const lineScrollTolerance = 8
// How long to keep re-issuing a scroll before giving up on positioning the line.
const lineScrollSettleDuration = 500
// How long to keep watching for late content that can push an already-positioned line back out of view.
const lineScrollLayoutWatchDuration = 3000
const lineScrollKeys = new Set([
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'End',
  'Home',
  'PageDown',
  'PageUp',
  ' ',
])

export function CodeBlob({
  blobLinesHandle,
  onCodeNavTokenSelected,
  codeSections,
  codeLineToSectionMap,
  validCodeNav,
  onLineStickOrUnstick,
  searchResults,
  focusedSearchResult,
}: {
  blobLinesHandle: React.RefObject<CodeLinesHandle | null>
  onCodeNavTokenSelected: (symbol: CodeNavData) => void
  codeSections?: Map<number, CodeSection[]>
  codeLineToSectionMap: CodeSectionLookup | undefined
  validCodeNav: boolean
  onLineStickOrUnstick: SetStickyLinesType
  searchResults: DefinitionOrReference[]
  focusedSearchResult: number | undefined
}) {
  const {rawLines, colorizedLines, stylingDirectives} = useSuspenseStyledBlob()
  const {tabSize} = useCurrentBlob()
  const hasBlame = !!useCurrentBlame()
  const [shouldMaterializeAllLines, setShouldMaterializeAllLines] = useState(false)
  const shouldUseCursor = useIsCursorEnabled()
  const lineHeight = useCurrentLineHeight('react-line-numbers')
  //defaulting to true for keyboard so that a symbol is not initially selected
  const [textSelection, setTextSelection] = useState({start: -1, end: -1, keyboard: true, displayStart: false})
  const [isTextAreaFocused, setIsTextAreaFocused] = useState(false)
  const parentRef = useRef<HTMLDivElement>(null)
  const [textOverlayShouldBeVisible, setTextOverlayShouldBeVisible] = useState(false)
  const cursorClickStartRef = useRef({startX: 0, startY: 0})
  const location = useLocation()
  const {hash} = location
  const routeIdentity = `${location.key}:${location.pathname}${location.search}${hash}`
  const highlightedLines = useHashHighlightedLines(hash)
  const path = usePath()
  const [manualScrollRequest, setManualScrollRequest] = useState<
    (ScrollRequestPayload & {routeIdentity: string; generation: number; consumed: boolean}) | undefined
  >()
  if (manualScrollRequest?.consumed && manualScrollRequest.routeIdentity !== routeIdentity) {
    setManualScrollRequest(undefined)
  }
  const scrollRequestGenerationRef = useRef(0)
  const activeScrollTargetRef = useRef<string | undefined>(undefined)
  const scrollCancelledByUserRef = useRef(false)
  const {copilotAccessAllowed} = useCopilotSettings()
  const refInfo = useRefInfo()
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const [contentWidth, setContentWidth] = useState<number | undefined>(undefined)
  // Tracks the text area's client width so it can be used as a fallback content width without reading the DOM ref
  // during render. Kept in sync by the same ResizeObserver that computes contentWidth below.
  const [textAreaClientWidth, setTextAreaClientWidth] = useState<number>()
  const {isServer: isSSR} = useRuntimeEnvironment()
  const cursorContainerRef = useRef<HTMLDivElement>(null)
  const wordWrapEnabled = useCodeViewOptions().codeWrappingOption.enabled

  const reRender = useManualRender()
  const currentLineRefMap = useLineElementMap()
  const stickyHeaderHeight = useStickyHeaderHeight()
  const sharedLineObserver = useSharedLineObserver(true, stickyHeaderHeight, onLineStickOrUnstick)
  // The highlighted line is derived from the URL hash by useHashHighlightedLines, so line-number
  // clicks no longer need to push state up through this callback. It is kept as a no-op to preserve
  // the existing prop interface on the line components.
  const onLineNumberClick = noOp
  const {findInFileOpen, setFindInFileOpen} = useFindInFileOpen()

  const splitCodeownersErrors = useSplitCodeownersErrorsContext()
  const {lines, plainTextLinesAsString, nonTruncatedLinesData} = useCodeLines(
    rawLines ?? [],
    stylingDirectives ?? null,
    codeSections ?? null,
    splitCodeownersErrors,
    codeLineToSectionMap,
  )
  const doOverlay =
    useShouldDoNoVirtualization(nonTruncatedLinesData.length) && colorizedLines && colorizedLines.length !== 0

  //we need this linesRef for a situation where a user clicks on a symbol that exists within a collapsed section.
  //we set a 0ms timeout for moving the screen to the appropriate scroll position, which then allows the component
  //to re-render and generate a new value for which lines should be present, but the timeout was set when only
  //the out of date set of lines was present. Using this ref instead of just 'lines' allows the line calculation
  //to have the up to date values for figuring out where the line is on the screen
  const linesRef = useRef<CodeLineData[]>(lines)
  useEffect(() => {
    linesRef.current = lines
  }, [lines])
  //this is used to clear all styling programmatically added to the page for collapsed/expanded lines. It is only
  //run if a new file is soft-navigated to.
  useEffect(() => {
    expandAllRows()
  }, [refInfo.currentOid, path])

  useEffect(() => {
    window.onbeforeprint = () => setShouldMaterializeAllLines(true)
    window.onafterprint = () => setShouldMaterializeAllLines(false)
  }, [])

  const collectWordWrapPreferencesOnLoad = useEffectEvent(() => {
    updateExpandPreferences(null, null, wordWrapEnabled)
  })
  useEffect(() => {
    //before we actually use the setting, we want to collect all users word wrap preferences for a while so we don't
    //accidentally unwrap their lines when they have line wrap selected
    collectWordWrapPreferencesOnLoad()
  }, [])

  useEffect(() => {
    // Do the announcement of the help menu to each user exactly one time. Hopefully they hear it!
    if (!(safeLocalStorage.getItem('heardHelpAnnouncement') === 'true')) {
      forceAnnouncementToScreenReaders('While the code is focused, press Alt+F1 for a menu of operations.', 2000)
      safeLocalStorage.setItem('heardHelpAnnouncement', 'true')
    }
  }, [])
  /**
   *  This useCallback is present to handle when the user hovers over a symbol
   *  and we add an on click event to that particular symbol. We add an attribute at
   *  the same time which we then later check for on future hovers to make sure we do
   *  not add the same event listener multiple times.
   *
   *  SymbolUnderPointer is changed constantly, basically whenever you move the mouse, so
   *  removing the event listener on a useEffect return does not work in this instance.
   *
   *  It is possible the entire thing can be refactored in some way to be able to remove
   *  the event listener on a useEffect return, but the only way this is a significant
   *  performance degradation is if a user systematically hovers over every symbol on the page, whereas
   *  this version saves us a lot of time on repeated hovers of the same symbol as well as time saved on
   *  moving the cursor around the page.
   */
  useSymbolUnderPointer(
    useCallback(
      (symbolUnderPointer: SymbolUnderPointer) => {
        function onTokenClick(e: Event) {
          const text = (e.target as Element).textContent ? (e.target as Element).textContent : ''
          if (onCodeNavTokenSelected && symbolUnderPointer) {
            onCodeNavTokenSelected({
              selectedText: text,
              lineNumber: symbolUnderPointer.lineNumber,
              offset: symbolUnderPointer.offset,
            })
            if (findInFileOpen) {
              setFindInFileOpen(false)
            }
          }
        }
        if (symbolUnderPointer && symbolUnderPointer?.node && !hasBlame) {
          if (!symbolUnderPointer.node.textContent || symbolUnderPointer.node.textContent.length < 3) return
          const element = symbolUnderPointer.node as Element
          if (!element || !element.hasAttribute || element.hasAttribute('clickadded')) {
            return
          }
          element.classList.add('pl-token')
          element.setAttribute('clickadded', 'true')
          element.addEventListener('click', onTokenClick)
        }
      },
      [findInFileOpen, hasBlame, setFindInFileOpen, onCodeNavTokenSelected],
    ),
    validCodeNav,
  )

  const scrollSelectedLineIntoView = useEffectEvent(({line, column}: ScrollRequestPayload) => {
    if (!blobLinesHandle.current) return false

    if (line < 10) {
      blobLinesHandle.current.scrollToTop()
    } else {
      //necessary because scrollToLine just scrolls to the X line visible on the page, which normally is
      //the same as the line number, but once a line has collapsed sections above it it is no longer equivalent
      const lineAccountingForCollapsedSections = getActualLineNumberBinarySearch(line, lines)
      if (lineAccountingForCollapsedSections === -1) {
        // the line was a part of a collapsed section
        const sectionArray = codeLineToSectionMap?.get(line)

        for (const section of sectionArray ?? []) {
          if (section && isRowCollapsed(section.startLine)) {
            //expand every collapsed section that this code line is part of
            expandRow(section.startLine)
          }
        }
        reRender()
      }
      // We use linesRef because expanding collapsed sections updates the visible-line
      // mapping on the following render.
      const updatedLineAccountingForCollapsedSections = getActualLineNumberBinarySearch(line, linesRef.current)
      if (updatedLineAccountingForCollapsedSections === -1) return false
      blobLinesHandle.current.scrollToLine(updatedLineAccountingForCollapsedSections, column)

      if (currentLineRefMap && !isLineInViewport(line)) {
        // -1 in 'set sticky lines' for the line number clears out all current sticky lines
        onLineStickOrUnstick(DELETE_STICKY_LINES_VALUE, true)
        applyStickyToParentStartLines(currentLineRefMap, line, codeLineToSectionMap, onLineStickOrUnstick)
      }
    }
    return true
  })

  const anchorLine = parseFileAnchor(hash).blobRange?.start?.line
  const scrollTarget = useMemo(() => {
    // A same-file code-nav click dispatches its request before navigating, so the request's route
    // identity is one navigation behind. Matching on the line it targets keeps the column.
    if (manualScrollRequest && !manualScrollRequest.consumed) {
      if (manualScrollRequest.routeIdentity === routeIdentity || manualScrollRequest.line === anchorLine) {
        return {...manualScrollRequest, kind: 'manual' as const}
      }
    }
    if (manualScrollRequest?.consumed && manualScrollRequest.routeIdentity === routeIdentity) return undefined
    return anchorLine ? {line: anchorLine, column: undefined, generation: 0, kind: 'anchor' as const} : undefined
  }, [anchorLine, manualScrollRequest, routeIdentity])

  const yieldToOutsideScroll = isFeatureEnabled('code_view_anchor_find_in_page_fix')
  // The sticky header resizes as the page scrolls past it, including when find-in-page scrolls the page. With the
  // flag on, the effect reads the height through an effect event so that resize doesn't restart the scroll. With it
  // off, the height stays a dependency and a resize restarts the scroll.
  const stickyHeaderRestartKey = yieldToOutsideScroll ? undefined : stickyHeaderHeight
  const getStickyHeaderHeight = useEffectEvent(() => stickyHeaderHeight)

  useLayoutEffect(() => {
    if (!scrollTarget) return

    if (activeScrollTargetRef.current !== routeIdentity) {
      activeScrollTargetRef.current = routeIdentity
      scrollCancelledByUserRef.current = false
    }

    const watchUntil = performance.now() + lineScrollLayoutWatchDuration
    let animationFrame = 0
    let watchTimeout = 0
    let settleDeadline = 0
    let stableFrames = 0
    let previousTop: number | undefined
    let settled = false
    // Where the page and the target were when the target last settled, or right after this loop last scrolled
    // to re-assert it. Unset until the first settle, and never set with the flag off.
    let expectedPosition: {scrollY: number; top: number | undefined} | undefined
    let finished = false

    const finish = () => {
      if (finished) return
      finished = true
      cancelAnimationFrame(animationFrame)
      teardown()
      if (scrollTarget.kind === 'manual') {
        setManualScrollRequest(request =>
          request?.generation === scrollTarget.generation ? {...request, consumed: true} : request,
        )
      }
    }

    // Only a deliberate interaction cancels the scroll. Inferring intent from scroll position also
    // catches the virtualizer's own corrections and browser scroll restoration, which permanently
    // stranded the line partway to its target.
    const cancel = () => {
      scrollCancelledByUserRef.current = true
      finish()
    }

    const cancelOnScrollKey = (event: KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (!event.defaultPrevented && lineScrollKeys.has(event.key)) cancel()
    }

    // The row is rendered, sits below the sticky header, is on screen, and has stopped moving.
    // Requiring an exact offset never matched: scrollToLine targets `line * lineHeight`, which
    // leaves the row at the code container's document offset rather than at the sticky header.
    const targetIsPositioned = () => {
      if (scrollTarget.line < 10) return window.scrollY <= lineScrollTolerance
      const target = document.getElementById(`LC${scrollTarget.line}`)
      if (!target) {
        previousTop = undefined
        return false
      }
      const {top} = target.getBoundingClientRect()
      const heldStill = previousTop !== undefined && Math.abs(top - previousTop) <= lineScrollTolerance
      previousTop = top
      return heldStill && top >= getStickyHeaderHeight() - lineScrollTolerance && top < window.innerHeight
    }

    const targetTop = () => document.getElementById(`LC${scrollTarget.line}`)?.getBoundingClientRect().top

    // Once the target has settled, late content can move it on screen without scrolling the page, and scroll
    // anchoring or virtualizer corrections can scroll the page without moving it on screen. When the page
    // scrolled and the target moved on screen, something else scrolled the page, such as find-in-page or an
    // in-page link, and scrolling back to the target would undo that.
    const scrolledAwayFrom = ({scrollY, top}: {scrollY: number; top: number | undefined}) => {
      if (scrollTarget.line < 10) return window.scrollY > lineScrollTolerance
      if (Math.abs(window.scrollY - scrollY) <= lineScrollTolerance) return false
      const currentTop = targetTop()
      return currentTop === undefined || top === undefined || Math.abs(currentTop - top) > lineScrollTolerance
    }

    const step = () => {
      if (scrollCancelledByUserRef.current) return finish()

      if (targetIsPositioned()) {
        stableFrames += 1
        if (stableFrames >= 2) {
          settled = true
          if (yieldToOutsideScroll) expectedPosition = {scrollY: window.scrollY, top: previousTop}
          // An anchor target stays armed so the observer below can re-assert it. A manual request is
          // one-shot and must not fire again once the URL owns the position.
          if (scrollTarget.kind === 'manual') finish()
          return
        }
      } else {
        stableFrames = 0
        // Until `expectedPosition` is set, only input cancels the scroll (see `cancel`). After that, an outside
        // scroll ends the watch, even one that lands while a re-assert is still in flight.
        if (expectedPosition && scrolledAwayFrom(expectedPosition)) return finish()
        if (performance.now() > settleDeadline) return finish()
        scrollSelectedLineIntoView(scrollTarget)
        if (expectedPosition) expectedPosition = {scrollY: window.scrollY, top: targetTop()}
      }
      animationFrame = requestAnimationFrame(step)
    }

    const start = () => {
      settled = false
      stableFrames = 0
      settleDeadline = performance.now() + lineScrollSettleDuration
      cancelAnimationFrame(animationFrame)
      step()
    }

    // The latest commit bar and deferred syntax highlighting land after the first scroll settles and
    // resize the document underneath the target.
    const observer = new ResizeObserver(() => {
      if (!settled || finished || performance.now() > watchUntil) return
      start()
    })

    function teardown() {
      window.clearTimeout(watchTimeout)
      observer.disconnect()
      window.removeEventListener('wheel', cancel)
      window.removeEventListener('touchstart', cancel)
      window.removeEventListener('pointerdown', cancel)
      window.removeEventListener('keydown', cancelOnScrollKey)
    }

    window.addEventListener('wheel', cancel, {passive: true})
    window.addEventListener('touchstart', cancel, {passive: true})
    window.addEventListener('pointerdown', cancel)
    window.addEventListener('keydown', cancelOnScrollKey)
    observer.observe(document.body)
    watchTimeout = window.setTimeout(finish, lineScrollLayoutWatchDuration)
    start()

    return () => {
      cancelAnimationFrame(animationFrame)
      teardown()
    }
  }, [routeIdentity, scrollTarget, yieldToOutsideScroll, stickyHeaderRestartKey])

  useEffect(() => {
    const currentTextArea = textAreaRef.current

    if (!currentTextArea) {
      setContentWidth(undefined)
      setTextAreaClientWidth(undefined)
      return
    }

    // Track the client width (used as a fallback content width) and, in cursor mode, the overflow width.
    // contentWidth is only set when the scrollable content is wider than the visible width. The setters use
    // functional updates that bail when the value is unchanged so resize ticks don't trigger wasted renders.
    const measure = (el: Element) => {
      const nextClientWidth = el.clientWidth
      setTextAreaClientWidth(prev => (prev === nextClientWidth ? prev : nextClientWidth))
      const nextContentWidth = shouldUseCursor && el.scrollWidth > el.clientWidth ? el.scrollWidth : undefined
      setContentWidth(prev => (prev === nextContentWidth ? prev : nextContentWidth))
    }
    measure(currentTextArea)

    // Coalesce observer-driven measurements to one per frame so a drag-resize doesn't re-render on every tick.
    let frame = 0
    const observer = new ResizeObserver(entries => {
      const entry = entries[0]
      if (!entry) return
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        measure(entry.target)
      })
    })
    observer.observe(currentTextArea)

    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [shouldUseCursor, path])

  useScrollLineIntoView(payload => {
    // An explicit request re-arms scrolling even if the user had scrolled away from the last target.
    scrollCancelledByUserRef.current = false
    setManualScrollRequest({
      ...payload,
      routeIdentity,
      generation: ++scrollRequestGenerationRef.current,
      consumed: false,
    })
  })

  const cursorRef = useRef<NavigationCursorHandle>(null)

  return (
    <SearchResultsProvider searchResults={searchResults} focusedSearchResult={focusedSearchResult}>
      <HighlightedLinesProvider highlightedLines={highlightedLines}>
        {lines.some(line => line.hiddenUnicode) && <HiddenUnicodeAlert />}
        <div
          className={codeBlobStyles.codeBlobWrapper}
          style={{paddingTop: hasBlame ? 0 : '8px', paddingBottom: hasBlame ? 0 : '8px'}}
        >
          <HighlightedLineMenuContainer>
            <CopilotButtonContainer>
              <div
                className={clsx(codeBlobStyles.codeBlobInner, hasBlame && codeBlobStyles.codeBlobInnerBlame)}
                ref={parentRef}
                onBlur={e => {
                  //only count the focus loss if it isn't a child
                  if (e.currentTarget.contains(e.relatedTarget)) {
                    return
                  }
                  setIsTextAreaFocused(false)
                }}
              >
                {shouldUseCursor && (
                  <div ref={cursorContainerRef} className={codeBlobStyles.cursorContainer}>
                    <div
                      style={{
                        width: contentWidth,
                        height: lineHeight * lines.length,
                      }}
                    >
                      <NavigationCursor
                        linesData={lines}
                        isBlame={false}
                        onCodeNavTokenSelected={onCodeNavTokenSelected}
                        isCursorVisible={!!isTextAreaFocused}
                        isVirtualized
                        textAreaRef={textAreaRef}
                        onCollapseToggle={reRender}
                        onLineStickOrUnstick={onLineStickOrUnstick}
                        tabSize={tabSize}
                        textSelection={textSelection}
                        shouldRenderOverlay={!!textOverlayShouldBeVisible}
                        ref={cursorRef}
                      />
                    </div>
                  </div>
                )}
                {shouldUseCursor && (
                  <ScopedCommands
                    commands={{
                      'code-view:page-down': () => cursorRef.current?.onPageDown(),
                      'code-view:page-up': () => cursorRef.current?.onPageUp(),
                      'code-view:inspect-symbol': () => cursorRef.current?.inspectSymbol(),
                      'code-view:highlight-line': () => cursorRef.current?.updateUrlForLineNumber(),
                      'code-view:open-line-menu': () => cursorRef.current?.showLineMenu(),
                    }}
                  >
                    <TextArea
                      textAreaRef={textAreaRef}
                      setTextOverlayShouldBeVisible={setTextOverlayShouldBeVisible}
                      setTextSelection={setTextSelection}
                      cursorClickStartRef={cursorClickStartRef}
                      parentRef={parentRef}
                      tabSize={tabSize}
                      plainTextLinesAsString={plainTextLinesAsString}
                      numLines={lines.length}
                      setIsTextAreaFocused={setIsTextAreaFocused}
                    />
                  </ScopedCommands>
                )}
                {hasBlame ? (
                  !isSSR ? (
                    <BlameLines
                      ref={blobLinesHandle}
                      linesData={lines}
                      colorizedLines={colorizedLines ? colorizedLines : undefined}
                      tabSize={tabSize}
                      copilotAccessAllowed={copilotAccessAllowed}
                      onLineNumberClick={onLineNumberClick}
                    />
                  ) : (
                    <BlameLinesSSR
                      linesData={lines}
                      colorizedLines={colorizedLines ? colorizedLines : undefined}
                      tabSize={tabSize}
                      copilotAccessAllowed={copilotAccessAllowed}
                      onLineNumberClick={onLineNumberClick}
                    />
                  )
                ) : !isSSR ? (
                  doOverlay ? (
                    <CodeLinesNoVirtualization
                      ref={blobLinesHandle}
                      linesData={lines}
                      nonTruncatedLinesData={nonTruncatedLinesData}
                      onLineNumberClick={onLineNumberClick}
                      colorizedLines={colorizedLines}
                      codeSections={codeSections}
                      codeLineToSectionMap={codeLineToSectionMap}
                      onLineStickOrUnstick={onLineStickOrUnstick}
                      sharedLineObserver={sharedLineObserver}
                      tabSize={tabSize}
                      textAreaRef={textAreaRef}
                      cursorContainerRef={cursorContainerRef}
                      contentWidth={contentWidth ?? textAreaClientWidth}
                      onCollapseToggle={reRender}
                      materializeAllLines={shouldMaterializeAllLines}
                      copilotAccessAllowed={copilotAccessAllowed}
                    />
                  ) : (
                    <CodeLines
                      ref={blobLinesHandle}
                      linesData={lines}
                      onLineNumberClick={onLineNumberClick}
                      codeSections={codeSections}
                      codeLineToSectionMap={codeLineToSectionMap}
                      onLineStickOrUnstick={onLineStickOrUnstick}
                      sharedLineObserver={sharedLineObserver}
                      tabSize={tabSize}
                      contentWidth={contentWidth}
                      cursorContainerRef={cursorContainerRef}
                      onCollapseToggle={reRender}
                      textAreaRef={textAreaRef}
                      materializeAllLines={shouldMaterializeAllLines}
                      copilotAccessAllowed={copilotAccessAllowed}
                    />
                  )
                ) : doOverlay ? (
                  <CodeLinesSSRNoVirtualization
                    linesData={lines}
                    nonTruncatedLinesData={nonTruncatedLinesData}
                    onLineNumberClick={onLineNumberClick}
                    colorizedLines={colorizedLines}
                    codeSections={codeSections}
                    codeLineToSectionMap={codeLineToSectionMap}
                    onLineStickOrUnstick={onLineStickOrUnstick}
                    tabSize={tabSize}
                    contentWidth={contentWidth}
                    copilotAccessAllowed={copilotAccessAllowed}
                    onCollapseToggle={reRender}
                  />
                ) : (
                  <CodeLinesSSR
                    linesData={lines}
                    onLineNumberClick={onLineNumberClick}
                    codeSections={codeSections}
                    codeLineToSectionMap={codeLineToSectionMap}
                    onLineStickOrUnstick={onLineStickOrUnstick}
                    tabSize={tabSize}
                    contentWidth={contentWidth}
                    copilotAccessAllowed={copilotAccessAllowed}
                    onCollapseToggle={reRender}
                  />
                )}
              </div>
            </CopilotButtonContainer>
          </HighlightedLineMenuContainer>
        </div>
      </HighlightedLinesProvider>
    </SearchResultsProvider>
  )
}
